import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { notification } from "antd";
import dayjs from "dayjs";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import axiosInstance from "@lhd/libs/axios";
import { chatCatalogRequest } from "./chatCatalog";
import { SERVICES } from "@lhd/constants";
import { getCompanyId } from "@lhd/utility";
import { user } from "@lhd/features/users/selector";
import ActionCable from "actioncable";
import { CABLE_RECONNECTED_EVENT, getSharedCable } from "@lhd/libs/actionCable";
import {
  ChatCategory,
  ChatStatus,
  ChatThread,
  ListMessItem,
  PriorityLabel,
} from "@lhd/types";
import { convertFieldKeyFilterValuesToNumbers } from "@lhd/features/ListMess/filterMappingUtils";
import {
  INQUIRY_TYPE_LABELS,
  inquiryTypeValuesFromFilter,
} from "./inquiryType";

export type OptionId = string | number;
export type IdToLabelMap = Record<string, string>;
export type LabelToIdMap = Record<string, OptionId>;

const buildOptionMappings = <T extends { id?: OptionId; name: string }>(
  items: T[],
) => {
  const idToLabel: IdToLabelMap = {};
  const labelToId: LabelToIdMap = {};

  items.forEach((item, index) => {
    const label = item.name;
    if (!label) return;

    const rawId = item.id ?? index;
    const idKey = String(rawId);
    idToLabel[idKey] = label;
    labelToId[label] = rawId;
    if ((item as any).value) {
      labelToId[(item as any).value] = rawId;
    }
  });

  return { idToLabel, labelToId };
};

export const buildCategoryMappings = (categories: ChatCategory[]) =>
  buildOptionMappings(categories);

export const buildStatusMappings = (statuses: ChatStatus[]) =>
  buildOptionMappings(statuses);

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "-";
  return parsed.format("YYYY/MM/DD HH:mm");
};

const toNumber = (value: unknown) => {
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const mapPriority = (
  priority?: number | string | null,
): PriorityLabel | undefined => {
  if (priority === null || priority === undefined) return undefined;
  if (typeof priority === "string") {
    if (
      priority === "最" ||
      priority === "高" ||
      priority === "中" ||
      priority === "低"
    )
      return priority;
    const numericValue = Number(priority);
    if (Number.isNaN(numericValue)) return undefined;
    return mapPriority(numericValue);
  }
  if (priority === 1) return "最";
  if (priority === 2) return "高";
  if (priority === 3) return "中";
  if (priority >= 4) return "低";
  return undefined;
};

const mapStatusLabel = (
  value: string | number | null | undefined,
  statusIdToLabel: IdToLabelMap,
) => {
  if (value === null || value === undefined || value === "") return "-";
  const stringKey = String(value);
  if (statusIdToLabel[stringKey]) {
    return statusIdToLabel[stringKey];
  }
  if (typeof value === "string" && Number.isNaN(Number(value))) return value;
  return String(value);
};

const mapCategoryLabel = (
  value: string | number | null | undefined,
  categoryIdToLabel: IdToLabelMap,
) => {
  if (value === null || value === undefined || value === "") return "-";
  const stringKey = String(value);
  if (categoryIdToLabel[stringKey]) {
    return categoryIdToLabel[stringKey];
  }
  if (typeof value === "string" && Number.isNaN(Number(value))) return value;
  return String(value);
};

export const mapThreadToListItem = (
  thread: ChatThread,
  statusIdToLabel: IdToLabelMap,
  categoryIdToLabel: IdToLabelMap,
): ListMessItem => {
  const formattedId = thread.id ? `#${String(thread.id)}` : "-";

  const statusName =
    thread.chat_status_name ??
    thread.status_name ??
    (thread.status_id
      ? mapStatusLabel(thread.status_id, statusIdToLabel)
      : "-");

  const categoryName =
    thread.chat_category_name ??
    thread.category_name ??
    (thread.category_id
      ? mapCategoryLabel(thread.category_id, categoryIdToLabel)
      : "-");

  return {
    id: formattedId,
    subject: thread.title ?? "-",
    creator:
      thread.created_by ??
      thread.created_by_name ??
      thread.unread_last_message_sender_name ??
      "-",
    status: statusName,
    category: categoryName,
    inquiryType: thread.inquiry_type
      ? INQUIRY_TYPE_LABELS[thread.inquiry_type] || thread.inquiry_type
      : "未分類",
    updatedDate: formatDateTime(thread.updated_at),
    unreadCount:
      thread.unread_count !== undefined &&
      thread.unread_count !== null &&
      thread.unread_count > 0
        ? thread.unread_count
        : undefined,
    priority: mapPriority(thread.priority),
    customerName:
      thread.created_by ??
      thread.created_by_name ??
      thread.customer_name ??
      thread.unread_last_message_sender_name ??
      undefined,
    customerCompany: thread.company_name ?? undefined,
    assignee: thread.assigned_to ?? thread.assigned_to_name ?? undefined,
    created_by_id: thread.created_by_id ?? undefined,
    assigned_to_id: thread.assigned_to_id ?? undefined,
    status_id: thread.status_id ?? undefined,
    company_id: thread.company_id ?? undefined,
  };
};

export const extractThreads = (
  payload: ChatThread[] | { data?: ChatThread[] } | any,
): ChatThread[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  // Handle nested data structure: { data: { data: [...] } }
  if (payload?.data?.data && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
};

const extractCategories = (
  payload: ChatCategory[] | { data?: ChatCategory[] },
): ChatCategory[] => {
  const rawData = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];
  return rawData.map((category: any) => ({
    ...category,
    isDefault: category.isDefault ?? category.is_default,
  }));
};

const extractStatuses = (
  payload: ChatStatus[] | { data?: ChatStatus[] },
): ChatStatus[] => {
  const rawData = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];
  return rawData.map((status: any) => ({
    ...status,
    isDefault: status.isDefault ?? status.is_default,
  }));
};

type UseFetchChatThreadsParams = {
  page: number;
  pageSize: number;
  search?: string;
  statusFilterSelected?: string[];
  categoryFilterSelected?: string[];
  statusIdToLabel?: IdToLabelMap;
  statusLabelToId?: LabelToIdMap;
  categoryIdToLabel?: IdToLabelMap;
  categoryLabelToId?: LabelToIdMap;
  columnFilters?: Record<string, any>;
  statusMappings?: { idToLabel: IdToLabelMap; labelToId: LabelToIdMap };
  categoryMappings?: { idToLabel: IdToLabelMap; labelToId: LabelToIdMap };
  refreshTrigger?: number; // Optional trigger to force refetch
  enabled?: boolean;
  filterParams?: Record<string, any>;
  __BLOCK_API__?: boolean;
};

type UseFetchChatThreadsResult = {
  data: ListMessItem[];
  total: number;
  loading: boolean;
};

const getAllMappedIds = <T>(
  selections: string[],
  mapping: Record<string, T>,
): T[] => {
  return selections
    .map((selection) => mapping[selection])
    .filter((id): id is T => id !== undefined);
};

// Helper to convert OptionId to number array
const toNumberArray = (ids: OptionId[]): number[] => {
  return ids
    .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
    .filter((id): id is number => !isNaN(id) && typeof id === "number");
};

// Helper to convert labels to number array via mapping
const convertLabelsToNumberArray = (
  labels: string[],
  labelToIdMap: LabelToIdMap | undefined,
): number[] => {
  if (!labelToIdMap || labels.length === 0) return [];
  const ids = labels
    .map((label) => labelToIdMap[label])
    .filter((id: OptionId | undefined): id is OptionId => id !== undefined);
  return toNumberArray(ids);
};

// Helper to add and deduplicate array values to formDataParams
const addToFormDataParams = (
  formDataParams: Record<string, number[]>,
  key: string,
  values: number[],
): void => {
  if (values.length === 0) return;
  if (!formDataParams[key]) {
    formDataParams[key] = [];
  }
  formDataParams[key].push(...values);
  formDataParams[key] = Array.from(new Set(formDataParams[key]));
};

const EMPTY_ARRAY: string[] = [];

export const useFetchChatThreads = ({
  page,
  pageSize,
  search,
  statusFilterSelected = EMPTY_ARRAY,
  categoryFilterSelected = EMPTY_ARRAY,
  statusIdToLabel,
  statusLabelToId,
  categoryIdToLabel,
  categoryLabelToId,
  columnFilters = {},
  statusMappings,
  categoryMappings,
  refreshTrigger,
  enabled = true,
  filterParams,
  __BLOCK_API__,
}: UseFetchChatThreadsParams): UseFetchChatThreadsResult => {
  const [data, setData] = useState<ListMessItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const effectiveStatusIdToLabel = useMemo(
    () =>
      statusIdToLabel && Object.keys(statusIdToLabel).length > 0
        ? statusIdToLabel
        : {},
    [statusIdToLabel],
  );
  const effectiveStatusLabelToId = useMemo(
    () =>
      statusLabelToId && Object.keys(statusLabelToId).length > 0
        ? statusLabelToId
        : {},
    [statusLabelToId],
  );

  const effectiveCategoryIdToLabel = useMemo(
    () =>
      categoryIdToLabel && Object.keys(categoryIdToLabel).length > 0
        ? categoryIdToLabel
        : {},
    [categoryIdToLabel],
  );
  const effectiveCategoryLabelToId = useMemo(
    () =>
      categoryLabelToId && Object.keys(categoryLabelToId).length > 0
        ? categoryLabelToId
        : {},
    [categoryLabelToId],
  );

  // Memoize stringified values for stable dependency comparison
  const columnFiltersKey = useMemo(
    () => JSON.stringify(columnFilters),
    [columnFilters],
  );
  const statusMappingsKey = useMemo(
    () => JSON.stringify(statusMappings?.labelToId || {}),
    [statusMappings],
  );
  const categoryMappingsKey = useMemo(
    () => JSON.stringify(categoryMappings?.labelToId || {}),
    [categoryMappings],
  );
  const statusFilterSelectedKey = useMemo(
    () => JSON.stringify(statusFilterSelected || []),
    [statusFilterSelected],
  );
  const categoryFilterSelectedKey = useMemo(
    () => JSON.stringify(categoryFilterSelected || []),
    [categoryFilterSelected],
  );
  const statusIdToLabelKey = useMemo(
    () => JSON.stringify(effectiveStatusIdToLabel || {}),
    [effectiveStatusIdToLabel],
  );
  const categoryIdToLabelKey = useMemo(
    () => JSON.stringify(effectiveCategoryIdToLabel || {}),
    [effectiveCategoryIdToLabel],
  );
  const filterParamsKey = useMemo(
    () => JSON.stringify(filterParams || {}),
    [filterParams],
  );

  const effectiveStatusIdToLabelRef = useRef(effectiveStatusIdToLabel);
  effectiveStatusIdToLabelRef.current = effectiveStatusIdToLabel;
  const effectiveCategoryIdToLabelRef = useRef(effectiveCategoryIdToLabel);
  effectiveCategoryIdToLabelRef.current = effectiveCategoryIdToLabel;
  const effectiveStatusLabelToIdRef = useRef(effectiveStatusLabelToId);
  effectiveStatusLabelToIdRef.current = effectiveStatusLabelToId;
  const effectiveCategoryLabelToIdRef = useRef(effectiveCategoryLabelToId);
  effectiveCategoryLabelToIdRef.current = effectiveCategoryLabelToId;
  const statusMappingsRef = useRef(statusMappings);
  statusMappingsRef.current = statusMappings;
  const categoryMappingsRef = useRef(categoryMappings);
  categoryMappingsRef.current = categoryMappings;

  // Ensure only the latest request can update UI state.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    // Skip fetching if disabled or blocked
    if (!enabled || __BLOCK_API__) {
      return;
    }

    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    let isActive = true;

    const isLatestRequest = () =>
      isActive && latestRequestIdRef.current === requestId;

    const fetchThreads = async () => {
      const companyId = getCompanyId();
      if (!companyId) {
        if (isLatestRequest()) {
          setData([]);
          setTotal(0);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        // Convert filterParams - convert label to ID for status and category
        const convertedFilterParams: Record<string, any> = {};
        if (filterParams) {
          Object.entries(filterParams).forEach(([key, value]) => {
            if (!Array.isArray(value)) {
              convertedFilterParams[key] = value;
              return;
            }

            // Only convert status/category filters to numbers, pass others as-is
            if (
              key.includes("chat_thread:status") ||
              key.includes("chat_thread:category")
            ) {
              const convertedIds = convertFieldKeyFilterValuesToNumbers(
                key,
                value,
                statusMappingsRef.current,
                categoryMappingsRef.current,
              );
              if (convertedIds.length > 0) {
                convertedFilterParams[key] = convertedIds;
              }
            } else {
              // For other filters, pass array values as-is
              if (value.length > 0) {
                convertedFilterParams[key] = value;
              }
            }
          });
        }

        const params: Record<string, any> = {
          company_id: companyId,
          page,
          per_page: pageSize,
          order_by: "last_message_at",
          order_direction: "desc",
          ...convertedFilterParams,
        };

        // Search parameter
        if (search) {
          params.filter_title = search;
        }

        // FormData parameters for arrays (will be sent as query params with array notation)
        const formDataParams: Record<string, any[]> = {};

        // Helper to convert OptionId to number
        const toNumberArray = (ids: OptionId[]): number[] => {
          return ids
            .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
            .filter((id): id is number => !isNaN(id) && typeof id === "number");
        };

        if (statusFilterSelected.length > 0) {
          const statusIds = getAllMappedIds(
            statusFilterSelected,
            effectiveStatusLabelToIdRef.current,
          );
          if (statusIds.length > 0) {
            formDataParams.status_id = toNumberArray(statusIds);
          }
        }

        if (categoryFilterSelected.length > 0) {
          const categoryIds = getAllMappedIds(
            categoryFilterSelected,
            effectiveCategoryLabelToIdRef.current,
          );
          if (categoryIds.length > 0) {
            formDataParams.category_id = toNumberArray(categoryIds);
          }
          if (categoryFilterSelected?.includes("__NULL__")) {
            formDataParams.filter_category = ["__NULL__"];
          }
        }

        // Map column filters to API params
        Object.keys(columnFilters).forEach((columnKey) => {
          const filter = columnFilters[columnKey];
          if (!filter) return;

          if (filter.search) {
            if (columnKey === "subject") {
              params.filter_title = filter.search;
            }
          }

          // Handle both standard text filter (string) and array
          const filterValues = Array.isArray(filter.filter)
            ? filter.filter
            : filter.filter
            ? [filter.filter]
            : [];

          if (filterValues.length > 0) {
            switch (columnKey) {
              case "subject":
                params.filter_title = filterValues[0];
                break;
              case "status":
                if (statusMappings?.labelToId) {
                  const statusIds = convertLabelsToNumberArray(
                    filterValues,
                    statusMappings.labelToId,
                  );
                  addToFormDataParams(formDataParams, "status_id", statusIds);
                }
                break;
              case "category":
                if (categoryMappings?.labelToId) {
                  const categoryIds = convertLabelsToNumberArray(
                    filterValues,
                    categoryMappings.labelToId,
                  );
                  addToFormDataParams(
                    formDataParams,
                    "category_id",
                    categoryIds,
                  );
                }
                break;
              case "inquiryType": {
                const inquiryTypes = inquiryTypeValuesFromFilter(filterValues);
                if (inquiryTypes.length > 0) {
                  params.inquiry_type = Array.from(new Set(inquiryTypes));
                }
                break;
              }
              case "assignee": {
                if (filterValues.length > 0) {
                  params.assigned_to = filterValues[0];
                }
                break;
              }
              case "creator": {
                if (filterValues.length > 0) {
                  params.created_by = filterValues[0];
                }
                break;
              }
              case "customerName": {
                if (filterValues.length > 0) {
                  params.created_by = filterValues[0];
                }
                break;
              }
              case "priority": {
                const priorityMap: Record<string, number> = {
                  最: 1,
                  高: 2,
                  中: 3,
                  低: 4,
                };
                const priorityNums = filterValues
                  .map((label: string) => priorityMap[label])
                  .filter((num: any): num is number => num !== undefined);
                addToFormDataParams(formDataParams, "priority", priorityNums);
                break;
              }
              case "id": {
                // Handle ID filter - extract numeric IDs from formatted strings like "#32"
                const ids = filterValues
                  .map((idStr: string) => {
                    const match = idStr.match(/^#?(\d+)$/);
                    return match ? match[1] : null;
                  })
                  .filter((id: any): id is string => id !== null);
                if (ids.length > 0) {
                  params.filter_id = ids[0];
                }
                break;
              }
              case "updatedDate": {
                if (filterValues.length > 0) {
                  params.filter_updated_at = filterValues[0];
                }
                break;
              }
              case "createdDate":
              case "created_at": {
                if (filterValues.length > 0) {
                  params.filter_created_at = filterValues[0];
                }
                break;
              }
            }
          }

          // Handle sort
          if (filter.sort) {
            if (columnKey === "updatedDate") {
              params.order_by = "last_message_at";
              params.order_direction = filter.sort === "asc" ? "asc" : "desc";
            } else if (columnKey === "created_at") {
              params.order_by = "created_at";
              params.order_direction = filter.sort === "asc" ? "asc" : "desc";
            }
          }
        });

        // Merge formData params into query params
        Object.keys(formDataParams).forEach((key) => {
          const arrayValue = formDataParams[key];
          if (Array.isArray(arrayValue) && arrayValue.length > 0) {
            params[key] = arrayValue;
          }
        });

        const response = await axiosInstanceV2.get<
          ChatThread[] | { data?: ChatThread[]; meta?: { total?: number } }
        >(SERVICES.CHAT_THREADS, {
          params,
        });

        if (!isLatestRequest()) return;

        const threads = extractThreads(response.data);
        const mappedData = threads.map((thread) =>
          mapThreadToListItem(
            thread,
            effectiveStatusIdToLabelRef.current,
            effectiveCategoryIdToLabelRef.current,
          ),
        );
        setData(mappedData);

        // Create mapping from name to ID for assignee and creator
        // This will be used in the next render cycle for filtering
        const assigneeNameToId = new Map<string, number | string>();
        const creatorNameToId = new Map<string, number | string>();
        mappedData.forEach((item) => {
          if (item.assignee && item.assigned_to_id) {
            assigneeNameToId.set(item.assignee, item.assigned_to_id);
          }
          if (item.creator && item.created_by_id) {
            creatorNameToId.set(item.creator, item.created_by_id);
          }
        });

        const headerTotal =
          response.headers?.["x-total"] ??
          response.headers?.["X-Total"] ??
          response.headers?.["x-total-count"];
        const headerTotalNumber = toNumber(headerTotal);
        const metaTotal =
          !Array.isArray(response.data) && (response.data as any)?.meta
            ? toNumber(
                (response.data as any)?.meta?.total ??
                  (response.data as any)?.meta?.pagination?.total,
              )
            : undefined;

        setTotal(headerTotalNumber ?? metaTotal ?? threads.length);
      } catch (error: any) {
        if (!isLatestRequest()) return;
        setData([]);
        setTotal(0);
        notification.error({
          message: "スレッド一覧の取得に失敗しました。",
          description:
            error?.response?.data?.message ??
            error?.message ??
            "しばらくしてからもう一度お試しください。",
        });
      } finally {
        if (isLatestRequest()) {
          setLoading(false);
        }
      }
    };

    fetchThreads();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    search,
    statusFilterSelectedKey,
    categoryFilterSelectedKey,
    statusIdToLabelKey,
    categoryIdToLabelKey,
    columnFiltersKey,
    statusMappingsKey,
    categoryMappingsKey,
    refreshTrigger,
    enabled,
    filterParamsKey,
  ]);

  return {
    data,
    total,
    loading,
  };
};

type UseFetchUnreadThreadsInfiniteParams = {
  enabled: boolean;
  pageSize?: number;
};

type UseFetchUnreadThreadsInfiniteResult = {
  data: ListMessItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
  refetch: () => void;
  markItemAsRead: (threadId: string) => void;
};

export const useFetchUnreadThreadsInfinite = ({
  enabled,
  pageSize = 15,
  search,
}: UseFetchUnreadThreadsInfiniteParams & {
  search?: string;
}): UseFetchUnreadThreadsInfiniteResult => {
  const auth = useSelector(user);
  const isSuperAdmin = useMemo(() => {
    return (
      !!auth?.is_admin &&
      (!!auth?.workers?.some((worker) => worker?.company?.is_admin) ||
        !!auth?.is_root_company)
    );
  }, [auth]);
  const [data, setData] = useState<ListMessItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [refetchKey, setRefetchKey] = useState(0);
  const isFirstLoadRef = useRef(true);
  const hasLoadedDataRef = useRef(false);

  const reset = useCallback(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
    setLoadingMore(false);
    isFirstLoadRef.current = true;
    hasLoadedDataRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      reset();
    }
  }, [enabled, reset]);

  useEffect(() => {
    reset();
  }, [search, reset]);

  useEffect(() => {
    if (!enabled) return;

    const fetchPage = async () => {
      const isInitial = page === 1;
      const showInitialLoading =
        isInitial && (isFirstLoadRef.current || !hasLoadedDataRef.current);
      const isPage1BackgroundRefetch = isInitial && !showInitialLoading;

      if (showInitialLoading) {
        setLoading(true);
      } else if (!isPage1BackgroundRefetch) {
        setLoadingMore(true);
      }

      try {
        const companyId = getCompanyId();
        if (!isSuperAdmin && !companyId) {
          if (isInitial) {
            setData([]);
          }
          setHasMore(false);
          return;
        }

        const url = `${SERVICES.CHAT_THREADS}/unread`;
        const params: any = {
          page,
          per_page: pageSize,
          count_only: false,
        };

        if (search) {
          params.search = search;
        }

        const response = await axiosInstanceV2.get(url, {
          params,
        });

        const threads = extractThreads(response.data);
        const mappedData = threads.map((thread) =>
          mapThreadToListItem(thread, {}, {}),
        );

        const totalFromHeader =
          parseInt(response.headers?.["x-total"] || "0") ||
          parseInt(response.headers?.["X-Total"] || "0") ||
          0;

        let nextHasMore = false;
        if (isInitial) {
          nextHasMore = mappedData.length < totalFromHeader;
        } else {
          const hasNewItems = mappedData.some(
            (newItem) => !data.some((oldItem) => oldItem.id === newItem.id),
          );
          nextHasMore =
            hasNewItems &&
            mappedData.length > 0 &&
            data.length + mappedData.length < totalFromHeader;

          if (mappedData.length < pageSize) {
            nextHasMore = false;
          }
        }
        setHasMore(nextHasMore);
        if (isInitial) {
          isFirstLoadRef.current = false;
        }
        if (mappedData.length > 0) {
          hasLoadedDataRef.current = true;
        }

        setData((prev) => {
          // Smart merge: compare and update only changed items
          const prevMap = new Map(prev.map((item) => [item.id, item]));
          const newMap = new Map(mappedData.map((item) => [item.id, item]));

          if (isInitial) {
            // Refetch case: compare new data with old data, preserve order from new data
            let hasAnyChange = false;
            const merged = mappedData.map((newItem) => {
              const oldItem = prevMap.get(newItem.id);
              if (oldItem) {
                const hasChanged =
                  newItem.subject !== oldItem.subject ||
                  newItem.status !== oldItem.status ||
                  newItem.creator !== oldItem.creator ||
                  newItem.customerCompany !== oldItem.customerCompany ||
                  newItem.category !== oldItem.category ||
                  newItem.priority !== oldItem.priority ||
                  newItem.unreadCount !== oldItem.unreadCount ||
                  newItem.updatedDate !== oldItem.updatedDate;

                if (hasChanged) {
                  hasAnyChange = true;
                  return { ...newItem, _updated: true };
                }
                return oldItem;
              }
              hasAnyChange = true;
              return { ...newItem, _updated: true };
            });

            if (prev.length !== merged.length) {
              hasAnyChange = true;
            } else if (!hasAnyChange) {
              for (const oldItem of prev) {
                if (!newMap.has(oldItem.id)) {
                  hasAnyChange = true;
                  break;
                }
              }
            }

            if (!hasAnyChange && prev.length === merged.length) {
              const orderChanged = prev.some(
                (item, index) => item.id !== merged[index]?.id,
              );
              if (!orderChanged) return prev;
            }

            return merged;
          }

          // LoadMore case: append new items, update existing ones
          let hasAnyChange = false;
          const updated = prev.map((item) => {
            const newItem = newMap.get(item.id);
            if (newItem) {
              const hasChanged =
                newItem.subject !== item.subject ||
                newItem.status !== item.status ||
                newItem.creator !== item.creator ||
                newItem.customerCompany !== item.customerCompany ||
                newItem.category !== item.category ||
                newItem.priority !== item.priority ||
                newItem.unreadCount !== item.unreadCount ||
                newItem.updatedDate !== item.updatedDate;

              if (hasChanged) {
                hasAnyChange = true;
                return { ...newItem, _updated: true };
              }
              return item;
            }
            return item;
          });

          const newItems = mappedData.filter((item) => !prevMap.has(item.id));
          if (newItems.length > 0) {
            hasAnyChange = true;
          }

          if (!hasAnyChange && newItems.length === 0) {
            return prev;
          }

          return [
            ...updated,
            ...newItems.map((item) => ({ ...item, _updated: true })),
          ];
        });
      } catch (error) {
        if (page === 1) {
          setData([]);
          hasLoadedDataRef.current = false;
        }
        setHasMore(false);
      } finally {
        if (showInitialLoading) {
          setLoading(false);
        } else if (!isPage1BackgroundRefetch) {
          setLoadingMore(false);
        }
      }
    };

    fetchPage();
  }, [enabled, isSuperAdmin, page, pageSize, refetchKey, search]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    setPage((prev) => prev + 1);
  }, [hasMore, loading, loadingMore]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    setPage(1);
    setRefetchKey((prev) => prev + 1);
  }, [enabled]);

  const markItemAsRead = useCallback((threadId: string) => {
    setData((prev) =>
      prev.map((item) => {
        const idStr = item.id.replace("#", "");
        const targetIdStr = threadId.replace("#", "");
        if (idStr === targetIdStr) {
          return { ...item, unreadCount: undefined };
        }
        return item;
      }),
    );
  }, []);

  return {
    data,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    reset,
    refetch,
    markItemAsRead,
  };
};

type UseFetchChatCategoriesParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
};

type UseFetchChatCategoriesResult = {
  data: ChatCategory[];
  total: number;
  loading: boolean;
  refetch: () => Promise<void>;
};

export const useFetchChatCategories = ({
  page = 1,
  pageSize = 100,
  search,
  enabled = true,
}: UseFetchChatCategoriesParams = {}): UseFetchChatCategoriesResult => {
  const [data, setData] = useState<ChatCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        per_page: pageSize,
      };
      if (search) {
        params.name = search;
      }

      const response = await axiosInstance.get<
        ChatCategory[] | { data?: ChatCategory[]; meta?: { total?: number } }
      >(SERVICES.CATEGORY_STATUS_COMPANIES, {
        params: {
          ...chatCatalogRequest("category", getCompanyId()).params,
          ...params,
        },
      });

      const categories = extractCategories(response.data);
      setData(categories);

      const headerTotal =
        response.headers?.["x-total"] ??
        response.headers?.["X-Total"] ??
        response.headers?.["x-total-count"];
      const headerTotalNumber = toNumber(headerTotal);
      const metaTotal =
        !Array.isArray(response.data) && (response.data as any)?.meta
          ? toNumber(
              (response.data as any)?.meta?.total ??
                (response.data as any)?.meta?.pagination?.total,
            )
          : undefined;

      const derivedTotal = headerTotalNumber ?? metaTotal;
      if (derivedTotal !== undefined) {
        setTotal(derivedTotal);
      } else {
        setTotal(categories.length);
      }
    } catch (error: any) {
      notification.error({
        message: "チャットカテゴリーの取得に失敗しました。",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "しばらくしてからもう一度お試しください。",
      });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    if (enabled) {
      fetchCategories();
    }
  }, [fetchCategories, refreshKey, enabled]);

  const refetch = useCallback(async () => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return {
    data,
    total,
    loading,
    refetch,
  };
};

type UseFetchChatStatusesParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
};

type UseFetchChatStatusesResult = {
  data: ChatStatus[];
  total: number;
  loading: boolean;
  refetch: () => Promise<void>;
};

export const useFetchChatStatuses = ({
  page = 1,
  pageSize = 100,
  search,
  enabled = true,
}: UseFetchChatStatusesParams = {}): UseFetchChatStatusesResult => {
  const [data, setData] = useState<ChatStatus[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page,
        per_page: pageSize,
      };
      if (search) {
        params.name = search;
      }

      const response = await axiosInstance.get<
        ChatStatus[] | { data?: ChatStatus[]; meta?: { total?: number } }
      >(SERVICES.CATEGORY_STATUS_COMPANIES, {
        params: {
          ...chatCatalogRequest("status", getCompanyId()).params,
          ...params,
        },
      });

      const statuses = extractStatuses(response.data);
      setData(statuses);

      const headerTotal =
        response.headers?.["x-total"] ??
        response.headers?.["X-Total"] ??
        response.headers?.["x-total-count"];
      const headerTotalNumber = toNumber(headerTotal);
      const metaTotal =
        !Array.isArray(response.data) && (response.data as any)?.meta
          ? toNumber(
              (response.data as any)?.meta?.total ??
                (response.data as any)?.meta?.pagination?.total,
            )
          : undefined;

      const derivedTotal = headerTotalNumber ?? metaTotal;
      if (derivedTotal !== undefined) {
        setTotal(derivedTotal);
      } else {
        setTotal(statuses.length);
      }
    } catch (error: any) {
      notification.error({
        message: "チャットステータスの取得に失敗しました。",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "しばらくしてからもう一度お試しください。",
      });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    if (enabled) {
      fetchStatuses();
    }
  }, [fetchStatuses, refreshKey, enabled]);

  const refetch = useCallback(async () => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return {
    data,
    total,
    loading,
    refetch,
  };
};

type UseCreateChatStatusParams = {
  name: string;
  color: string;
};

type UseUpdateChatStatusParams = {
  id: number | string;
  name?: string;
  color?: string;
};

type UseDeleteChatStatusParams = {
  id: number | string;
};

export const useCreateChatStatus = () => {
  const [loading, setLoading] = useState(false);

  const createStatus = async (params: UseCreateChatStatusParams) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", params.name);
      formData.append("color", params.color);

      const response = await axiosInstanceV2.post<ChatStatus>(
        SERVICES.CHAT_STATUSES,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      notification.error({
        message: "ステータスの作成に失敗しました。",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "しばらくしてからもう一度お試しください。",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createStatus, loading };
};

export const useUpdateChatStatus = () => {
  const [loading, setLoading] = useState(false);

  const updateStatus = async (params: UseUpdateChatStatusParams) => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (params.name !== undefined) {
        formData.append("name", params.name);
      }
      if (params.color !== undefined) {
        formData.append("color", params.color);
      }

      const response = await axiosInstanceV2.patch<ChatStatus>(
        `${SERVICES.CHAT_STATUSES}/${params.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      notification.error({
        message: "ステータスの更新に失敗しました。",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "しばらくしてからもう一度お試しください。",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateStatus, loading };
};

export const useDeleteChatStatus = () => {
  const [loading, setLoading] = useState(false);

  const deleteStatus = async (params: UseDeleteChatStatusParams) => {
    setLoading(true);
    try {
      await axiosInstanceV2.delete(`${SERVICES.CHAT_STATUSES}/${params.id}`);

      notification.success({
        message: "ステータスの削除が完了しました。",
      });
    } catch (error: any) {
      // Don't show notification for 422 errors, let the caller handle it
      if (error?.response?.status !== 422) {
        notification.error({
          message: "ステータスの削除に失敗しました。",
          description:
            error?.response?.data?.message ??
            error?.message ??
            "しばらくしてからもう一度お試しください。",
        });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteStatus, loading };
};

type UseCreateChatCategoryParams = {
  name: string;
  description?: string;
};

type UseUpdateChatCategoryParams = {
  id: number | string;
  name?: string;
  description?: string;
};

type UseDeleteChatCategoryParams = {
  id: number | string;
};

export const useCreateChatCategory = () => {
  const [loading, setLoading] = useState(false);

  const createCategory = async (params: UseCreateChatCategoryParams) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", params.name);
      if (params.description !== undefined) {
        formData.append("description", params.description);
      }

      const response = await axiosInstanceV2.post<ChatCategory>(
        SERVICES.CHAT_CATEGORIES,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      notification.error({
        message: "カテゴリーの作成に失敗しました。",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "しばらくしてからもう一度お試しください。",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createCategory, loading };
};

export const useUpdateChatCategory = () => {
  const [loading, setLoading] = useState(false);

  const updateCategory = async (params: UseUpdateChatCategoryParams) => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (params.name !== undefined) {
        formData.append("name", params.name);
      }
      if (params.description !== undefined) {
        formData.append("description", params.description);
      }

      const response = await axiosInstanceV2.patch<ChatCategory>(
        `${SERVICES.CHAT_CATEGORIES}/${params.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      notification.error({
        message: "カテゴリーの更新に失敗しました。",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "しばらくしてからもう一度お試しください。",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateCategory, loading };
};

export const useDeleteChatCategory = () => {
  const [loading, setLoading] = useState(false);

  const deleteCategory = async (params: UseDeleteChatCategoryParams) => {
    setLoading(true);
    try {
      await axiosInstanceV2.delete(`${SERVICES.CHAT_CATEGORIES}/${params.id}`);

      notification.success({
        message: "カテゴリーの削除が完了しました。",
      });
    } catch (error: any) {
      // Don't show notification for 422 errors with msg (handled by caller)
      if (!(error?.response?.status === 422 && error?.response?.data?.msg)) {
        notification.error({
          message: "カテゴリーの削除に失敗しました。",
          description:
            error?.response?.data?.message ??
            error?.message ??
            "しばらくしてからもう一度お試しください。",
        });
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteCategory, loading };
};

type UseFetchTotalUnreadCountParams = {
  isModalOpen?: boolean;
  refetchDetailedList?: () => void;
};

type UseFetchTotalUnreadCountResult = {
  totalUnreadCount: number;
};

export const useFetchTotalUnreadCount = (
  params?: UseFetchTotalUnreadCountParams,
): UseFetchTotalUnreadCountResult => {
  const { isModalOpen = false, refetchDetailedList } = params || {};
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const mountedRef = useRef(true);
  const subscriptionRef = useRef<ActionCable.Channel | null>(null);
  const isModalOpenRef = useRef(isModalOpen);
  const refetchDetailedListRef = useRef(refetchDetailedList);
  const previousCountRef = useRef(0);
  const refetchUnreadCountTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Update refs when params change
  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
    refetchDetailedListRef.current = refetchDetailedList;
  }, [isModalOpen, refetchDetailedList]);

  // Get companyId outside useEffect so it can be used in dependency array
  const companyId = getCompanyId();

  useEffect(() => {
    mountedRef.current = true;
    let ignore = false;

    const setupSubscription = () => {
      const cable = getSharedCable();
      if (!cable) return;

      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (_) {
          // ignore
        }
        subscriptionRef.current = null;
      }

      try {
        subscriptionRef.current = cable.subscriptions.create(
          {
            channel: "UnreadThreadsCountChannel",
          },
          {
            received: (data: any) => {
              if (ignore || !mountedRef.current) return;

              let newCount: number | undefined;
              if (
                data.event === "unread_count_updated" &&
                typeof data.count === "number"
              ) {
                newCount = data.count;
              } else if (typeof data.count === "number") {
                newCount = data.count;
              } else if (typeof data.unread_count === "number") {
                newCount = data.unread_count;
              }

              if (newCount !== undefined) {
                previousCountRef.current = newCount;
                setTotalUnreadCount(newCount);

                if (isModalOpenRef.current && refetchDetailedListRef.current) {
                  refetchDetailedListRef.current();
                }
              }
            },
          },
        );
      } catch (error) {
        // Silently handle WebSocket setup errors
      }
    };

    setupSubscription();
    window.addEventListener(CABLE_RECONNECTED_EVENT, setupSubscription);

    return () => {
      ignore = true;
      mountedRef.current = false;
      window.removeEventListener(CABLE_RECONNECTED_EVENT, setupSubscription);
      if (refetchUnreadCountTimeoutRef.current) {
        clearTimeout(refetchUnreadCountTimeoutRef.current);
        refetchUnreadCountTimeoutRef.current = null;
      }
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [companyId]);
  return {
    totalUnreadCount,
  };
};

export const useMarkThreadAsRead = () => {
  const [loading, setLoading] = useState(false);

  const markAsRead = useCallback(async (threadId: string | number) => {
    setLoading(true);
    try {
      const id =
        typeof threadId === "string" ? threadId.replace("#", "") : threadId;
      await axiosInstanceV2.post(`${SERVICES.CHAT_THREADS}/${id}/read`);
    } catch (error: any) {
      console.error("Failed to mark thread as read:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  return { markAsRead, loading };
};

export const useCheckChatPermission = () => {
  const auth = useSelector(user);

  const checkPermission = useCallback(
    async (threadId: string, options?: { showError?: boolean }) => {
      const { showError = true } = options || {};
      if (!auth) return null;

      try {
        const sanitizedId = threadId.replace("#", "");
        const { data } = await axiosInstanceV2.get(
          `${SERVICES.CHAT_THREADS}/${sanitizedId}`,
        );

        const isSubAdmin = !!auth.is_admin;
        const userCompanies = auth.companies || [];
        const hasCompanyPermission = userCompanies.some(
          (c: any) => String(c.id) === String(data.company_id),
        );

        if (isSubAdmin || hasCompanyPermission) {
          return data;
        } else {
          if (showError) {
            notification.error({
              message: "権限がありません",
            });
          }
          return null;
        }
      } catch (error: any) {
        if (showError) {
          notification.error({
            message:
              error?.response?.status === 403
                ? "権限がありません"
                : "スレッド情報の取得に失敗しました",
          });
        }
        return null;
      }
    },
    [auth],
  );

  return useMemo(() => ({ checkPermission }), [checkPermission]);
};

type UseCheckChatCategoryParams = {
  id: number | string;
};

type CheckChatCategoryResponse = {
  in_use: boolean;
  message?: string;
};

export const useCheckChatCategory = () => {
  const [loading, setLoading] = useState(false);

  const checkCategory = async (
    params: UseCheckChatCategoryParams,
  ): Promise<CheckChatCategoryResponse> => {
    setLoading(true);
    try {
      const response = await axiosInstanceV2.get<CheckChatCategoryResponse>(
        `${SERVICES.CHAT_CATEGORIES}/${params.id}/check`,
      );

      return response.data;
    } catch (error: any) {
      return {
        in_use: false,
        message: undefined,
      };
    } finally {
      setLoading(false);
    }
  };

  return { checkCategory, loading };
};

type UseCheckChatStatusParams = {
  id: number | string;
};

type CheckChatStatusResponse = {
  in_use: boolean;
  message?: string;
};

export const useCheckChatStatus = () => {
  const [loading, setLoading] = useState(false);

  const checkStatus = async (
    params: UseCheckChatStatusParams,
  ): Promise<CheckChatStatusResponse> => {
    setLoading(true);
    try {
      const response = await axiosInstanceV2.get<CheckChatStatusResponse>(
        `${SERVICES.CHAT_STATUSES}/${params.id}/check`,
      );

      return response.data;
    } catch (error: any) {
      return {
        in_use: false,
        message: undefined,
      };
    } finally {
      setLoading(false);
    }
  };

  return { checkStatus, loading };
};

// Reorder chat status by id and new position (0-based)
export async function reorderChatStatus(
  id: number | string,
  sort_order_position: number,
) {
  const url = `${SERVICES.CHAT_STATUSES}/${id}/reorder`;
  return axiosInstanceV2.put(
    url,
    { sort_order_position },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
}

// Reorder chat category by id and new position (0-based)
export async function reorderChatCategory(
  id: number | string,
  sort_order_position: number,
) {
  const url = `${SERVICES.CHAT_CATEGORIES}/${id}/reorder`;
  return axiosInstanceV2.put(
    url,
    { sort_order_position },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
}

export interface ChatThreadFilterOptionsParams {
  field_key?: string;
  active_filters?: Record<string, string[]>;
  active_search?: Record<string, string>;
  filter_order?: string[];
}

export const useFetchChatThreadFilterOptions = (
  params: ChatThreadFilterOptionsParams | null,
  enabled?: boolean,
) => {
  return useQuery(
    [
      "CHAT_THREADS",
      "filter_options",
      params?.field_key,
      params?.active_filters,
      params?.active_search,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;
      const company_id = getCompanyId();
      if (!company_id) return null;

      const res = await axiosInstanceV2({
        method: "post",
        url: `${SERVICES.CHAT_THREADS}/filter_options`,
        data: {
          company_id,
          ...params,
        },
      });

      return res.data;
    },
    {
      enabled: !!params?.field_key && (enabled ?? true),
      staleTime: 5 * 60 * 1000,
    },
  );
};
